// Turning pastes, drops and phone input into cards. Uploads and link previews happen here.
import { SHARED_BOARDS, type Pos } from '../../shared/doc.js';
import { addCard, SILENT, updateCard, type Where } from '../../shared/ops.js';
import { api, uploadImage, type Preview } from '../api.js';
import type { ProjectConn } from '../doc/useProject.js';
import { isHttpUrl } from './embed.js';

export const MAX_UPLOAD = 15 * 1024 * 1024;
export type Toast = (message: string) => void;

/** Fills in a link card's preview once the server fetches it. Written with SILENT: an async
 * result arriving after the paste shouldn't become its own undo step (⌘Z should remove the
 * card the user pasted, not revert it to "Fetching preview…"). */
export async function fetchPreview(conn: ProjectConn, projectId: string, cardId: string, url: string) {
  try {
    const p = await api<Preview>('POST', `/api/projects/${projectId}/link-preview`, { url });
    updateCard(conn.doc, conn.me, cardId, {
      title: p.title,
      description: p.description,
      siteName: p.siteName,
      imageAssetId: p.imageAssetId,
      previewStatus: p.status,
    }, SILENT);
  } catch {
    updateCard(conn.doc, conn.me, cardId, { previewStatus: 'failed' }, SILENT);
  }
}

export function createLink(conn: ProjectConn, projectId: string, where: Where, url: string): string {
  const id = addCard(conn.doc, conn.me, { kind: 'link', url: url.trim(), previewStatus: 'pending' }, where);
  void fetchPreview(conn, projectId, id, url.trim());
  return id;
}

/** Text becomes a link card when it is a single URL, otherwise a note. */
export function createFromText(conn: ProjectConn, projectId: string, where: Where, text: string): string | null {
  const t = text.trim();
  if (!t) return null;
  if (isHttpUrl(t)) return createLink(conn, projectId, where, t);
  return addCard(conn.doc, conn.me, { kind: 'note', text: t.slice(0, 20_000) }, where);
}

/** Uploads images one by one; each failure gets its own message and the rest continue. */
export async function createImages(conn: ProjectConn, projectId: string, files: File[], whereFor: (i: number) => Where, toast: Toast): Promise<string[]> {
  const ids: string[] = [];
  let i = 0;
  for (const file of files) {
    if (file.size > MAX_UPLOAD) {
      toast(`${file.name}: Files can be at most 15 MiB.`);
      continue;
    }
    try {
      const asset = await uploadImage(projectId, file, file.name);
      ids.push(addCard(conn.doc, conn.me, { kind: 'image', assetId: asset.id, width: asset.width, height: asset.height }, whereFor(i++)));
    } catch (err) {
      toast(`${file.name}: ${(err as Error).message}`);
    }
  }
  return ids;
}

export const onBoard = (boardId: string, pos: Pos): Where => ({ boardId, pos });
export const inSpark: Where = { spark: true };
export { SHARED_BOARDS };
